import { useId, useState, type FormEvent, type ReactNode } from "react";
import { ChevronDown, ChevronUp, SendHorizontal } from "lucide-react";
import { cn } from "@hey404/ui";
import { Avatar } from "./avatar";
import { btnPrimary, inputClass } from "./admin-ui";
import { timeAgo } from "./lib/time";

/**
 * Shared pieces for the class comments and the course forum, so both read as
 * one system (V30): a section header with the count chip, an author line
 * (avatar, bold name, tags, relative time), small tags, quiet text actions and
 * a composer with a visible label and one primary button.
 */

export function CommunityHeader({
  title,
  count,
  description,
  action,
}: {
  title: string;
  count?: number;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0">
        <p className="text-ink-soft flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.06em]">
          <span
            aria-hidden="true"
            className="border-ink bg-bubble border-chip inline-block size-2.5 rotate-[-15deg]"
          />
          Comunidad
        </p>
        <div className="mt-2 flex items-center gap-3">
          <h2 className="font-display text-2xl font-extrabold sm:text-3xl">
            {title}
          </h2>
          {count !== undefined && (
            <span className="border-ink bg-lemon border-chip rotate-[-2deg] px-2 py-0.5 font-mono text-xs font-bold">
              {count}
            </span>
          )}
        </div>
        {description && (
          <p className="text-ink-soft mt-1.5 text-sm leading-relaxed">
            {description}
          </p>
        )}
      </div>
      {action}
    </header>
  );
}

export function CommunityTag({
  tone = "bg-paper",
  children,
}: {
  tone?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        "border-ink border-chip inline-flex items-center gap-1 px-1.5 py-0.5 font-mono text-[11px] font-bold uppercase leading-none",
        tone,
      )}
    >
      {children}
    </span>
  );
}

/** Who wrote it and when: avatar, bold name, optional tags, relative time. */
export function PostAuthor({
  name,
  avatarUrl,
  createdAt,
  tone = "bg-grape",
  small,
  children,
}: {
  name: string;
  avatarUrl?: string | null;
  createdAt: string;
  tone?: string;
  small?: boolean;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <Avatar
        name={name}
        url={avatarUrl}
        className={cn(
          "border-ink",
          small ? "h-8 w-8 text-[11px]" : "h-9 w-9 text-xs",
          tone,
        )}
      />
      <strong className="person-name text-sm">{name}</strong>
      {children}
      <time
        dateTime={createdAt}
        title={new Date(createdAt).toLocaleString("es-AR")}
        className="text-ink-soft font-mono text-xs"
      >
        · {timeAgo(createdAt)}
      </time>
    </div>
  );
}

/** Quiet inline action under a post: "Responder", "Marcar como respuesta". */
export function TextAction({
  icon: Icon,
  children,
  onClick,
  disabled,
}: {
  icon?: (props: { size?: number; "aria-hidden"?: boolean }) => ReactNode;
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="text-ink-soft hover:text-ink hover:bg-paper-dim rounded-control -ml-2 inline-flex cursor-pointer items-center gap-1.5 px-2 py-1.5 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50"
    >
      {Icon && <Icon size={14} aria-hidden={true} />}
      {children}
    </button>
  );
}

/**
 * Write a comment, a reply or a new thread: a visible label, the field(s), a
 * footer with an optional note on the left and the one primary button.
 */
export function Composer({
  label,
  value,
  onChange,
  placeholder,
  submitLabel,
  busy,
  disabled,
  onSubmit,
  note,
  rows = 3,
  before,
  avatar,
  textareaRef,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  submitLabel: string;
  busy?: boolean;
  disabled?: boolean;
  onSubmit: (event: FormEvent) => void;
  /** Left of the button: "Respondiendo a…", a hint about moderation, etc. */
  note?: ReactNode;
  rows?: number;
  /** Extra fields above the textarea (the forum's title). */
  before?: ReactNode;
  avatar?: ReactNode;
  textareaRef?: React.Ref<HTMLTextAreaElement>;
}) {
  const id = useId();
  return (
    <form onSubmit={onSubmit} className="flex gap-3">
      {avatar}
      <div className="min-w-0 flex-1">
        {before}
        <label htmlFor={id} className="font-mono text-sm font-bold">
          {label}
        </label>
        <textarea
          ref={textareaRef}
          id={id}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          rows={rows}
          placeholder={placeholder}
          className={cn(inputClass, "mt-1.5 resize-y px-3 py-2.5 text-base")}
        />
        <div className="mt-2.5 flex flex-wrap items-center justify-between gap-3">
          <div className="text-ink-soft min-w-0 text-xs">{note}</div>
          <button
            disabled={disabled || busy || !value.trim()}
            className={cn(
              btnPrimary,
              "ml-auto px-4 py-2.5 text-sm disabled:cursor-not-allowed",
            )}
          >
            {busy ? "Publicando…" : submitLabel}
          </button>
        </div>
      </div>
    </form>
  );
}

/**
 * A post's text, clamped to three lines when it's long, with "Leer más" to
 * open it in place. Keeps long comments from eating the page.
 */
export function PostBody({
  text,
  className,
}: {
  text: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  // ~3 lines at the column width; shorter text never gets the toggle.
  const long = text.length > 220 || text.split("\n").length > 3;
  return (
    <div className={className}>
      <p
        className={cn(
          "whitespace-pre-wrap text-sm leading-relaxed",
          long && !open && "line-clamp-3",
        )}
      >
        {text}
      </p>
      {long && (
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="person-name mt-0.5 cursor-pointer text-sm underline-offset-4 hover:underline"
        >
          {open ? "Leer menos" : "Leer más"}
        </button>
      )}
    </div>
  );
}

/** "Ver todos los comentarios (12)" / "Ver menos" under a capped list. */
export function ShowAllButton({
  expanded,
  hidden,
  label,
  onToggle,
}: {
  expanded: boolean;
  /** How many are hidden while collapsed. */
  hidden: number;
  /** Collapsed label: "Ver todos los comentarios", "Ver todas las respuestas". */
  label: string;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-expanded={expanded}
      onClick={onToggle}
      className="border-ink bg-paper border-chip rounded-control hover:bg-paper-dim mt-3 flex w-full cursor-pointer items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold transition-colors"
    >
      {expanded ? (
        <>
          Ver menos <ChevronUp size={15} aria-hidden="true" />
        </>
      ) : (
        <>
          {label}
          <span className="bg-ink/10 rounded-full px-1.5 py-0.5 font-mono text-[11px] leading-none">
            +{hidden}
          </span>
          <ChevronDown size={15} aria-hidden="true" />
        </>
      )}
    </button>
  );
}

/**
 * Social-style composer: avatar + a one-line field that grows as you type,
 * with the send button inside it. The label stays for screen readers; the
 * placeholder carries the visible prompt. Enter sends, Shift+Enter breaks
 * the line.
 */
export function ChatComposer({
  label,
  value,
  onChange,
  placeholder,
  onSubmit,
  busy,
  avatar,
  context,
  textareaRef,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  onSubmit: (event: FormEvent) => void;
  busy?: boolean;
  avatar?: ReactNode;
  /** Above the field: "Respondiendo a Valeria · ✕". */
  context?: ReactNode;
  textareaRef?: React.Ref<HTMLTextAreaElement>;
}) {
  const id = useId();
  const canSend = Boolean(value.trim()) && !busy;
  return (
    <form onSubmit={onSubmit} className="flex items-start gap-2.5">
      {avatar}
      <div className="min-w-0 flex-1">
        {context && <div className="mb-1.5">{context}</div>}
        <label htmlFor={id} className="sr-only">
          {label}
        </label>
        <div className="border-ink bg-paper border-chip rounded-control flex items-end gap-1 py-1 pl-3 pr-1 focus-within:shadow-[0_0_0_3px_var(--focus-ring)]">
          <textarea
            ref={textareaRef}
            id={id}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                if (canSend) event.currentTarget.form?.requestSubmit();
              }
            }}
            rows={1}
            placeholder={placeholder}
            className="bar-input placeholder:text-ink-soft max-h-40 min-h-9 min-w-0 flex-1 resize-none bg-transparent py-1.5 text-[15px] leading-snug [field-sizing:content]"
          />
          <button
            disabled={!canSend}
            aria-label={busy ? "Publicando…" : "Publicar"}
            title="Publicar (Enter)"
            className={cn(
              "border-ink border-chip rounded-control grid h-9 w-9 shrink-0 place-items-center transition-colors",
              canSend
                ? "bg-bubble hover:bg-ink hover:text-paper cursor-pointer"
                : "bg-paper-dim text-ink-soft cursor-not-allowed border-transparent",
            )}
          >
            <SendHorizontal size={16} aria-hidden="true" />
          </button>
        </div>
      </div>
    </form>
  );
}

/**
 * One comment or reply, YouTube-style: a round avatar, then the bold name and
 * the time on one line, the text, a row of small actions and, for top-level
 * comments, a "▾ N respuestas" toggle with the replies folded under it.
 * No bubble or card, so a long conversation stays light.
 */
export function CommentBubble({
  name,
  avatarUrl,
  createdAt,
  tags,
  body,
  actions,
  menu,
  small,
  highlight,
  tone = "bg-lemon",
  replyCount = 0,
  repliesOpen = false,
  onToggleReplies,
  children,
}: {
  name: string;
  avatarUrl?: string | null;
  createdAt: string;
  tags?: ReactNode;
  body: string;
  /** Small actions under the text: Responder, Marcar como respuesta… */
  actions?: ReactNode;
  /** A ⋮ menu for moderators, at the right. */
  menu?: ReactNode;
  small?: boolean;
  /** Accepted answer: a soft acid wash behind the reply. */
  highlight?: boolean;
  tone?: string;
  /** Replies under this comment; > 0 shows the toggle. */
  replyCount?: number;
  repliesOpen?: boolean;
  onToggleReplies?: () => void;
  /** The replies, rendered only while open. */
  children?: ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex items-start gap-3",
        highlight && "bg-acid/25 rounded-control -mx-2 px-2 py-1.5",
      )}
    >
      <Avatar
        name={name}
        url={avatarUrl}
        className={cn(
          "border-ink shrink-0 rounded-full",
          small ? "h-7 w-7 text-[10px]" : "h-9 w-9 text-[11px]",
          tone,
        )}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[13px]">
              <strong className="person-name">{name}</strong>
              <time
                dateTime={createdAt}
                title={new Date(createdAt).toLocaleString("es-AR")}
                className="text-ink-soft text-xs"
              >
                {timeAgo(createdAt)}
              </time>
              {tags}
            </div>
            <PostBody text={body} className="mt-0.5" />
          </div>
          {menu}
        </div>
        {actions && (
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
            {actions}
          </div>
        )}
        {replyCount > 0 && onToggleReplies && (
          <button
            type="button"
            aria-expanded={repliesOpen}
            onClick={onToggleReplies}
            className="person-name hover:bg-paper-dim rounded-control -ml-2 mt-1 inline-flex cursor-pointer items-center gap-1.5 px-2 py-1 text-sm transition-colors"
          >
            {repliesOpen ? (
              <ChevronUp size={16} aria-hidden="true" />
            ) : (
              <ChevronDown size={16} aria-hidden="true" />
            )}
            {replyCount} {replyCount === 1 ? "respuesta" : "respuestas"}
          </button>
        )}
        {repliesOpen && children && <div className="mt-2">{children}</div>}
      </div>
    </div>
  );
}

/** Small text action for the meta row under a bubble. */
export function MetaAction({
  children,
  onClick,
  strong,
}: {
  children: ReactNode;
  onClick: () => void;
  strong?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "person-name hover:bg-paper-dim rounded-control -ml-2 cursor-pointer px-2 py-1 transition-colors",
        strong && "text-ink",
      )}
    >
      {children}
    </button>
  );
}
